import React from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';

/**
 * Stadium Hang Jebat Melaka - simplified 3D representation
 * Curved oval structure with red pillars and roof
 */
export function StadiumMelaka({ position = [0, 0, -180] }) {
  // Stadium dimensions
  const stadiumRadius = 80;
  const stadiumDepth = 45;
  const stadiumHeight = 22;
  
  // Generate curved seating rows
  const segments = 24;
  const seats = [];
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI - Math.PI / 2;
    const x = Math.cos(angle) * stadiumRadius;
    const z = Math.sin(angle) * stadiumDepth;
    seats.push({ x, z, angle });
  }

  return (
    <group position={position}>
      {/* Main stadium base - curved wall */}
      {seats.map((seat, i) => (
        <group key={i} position={[seat.x, 0, seat.z]} rotation={[0, -seat.angle - Math.PI/2, 0]}>
          {/* Red pillars */}
          <mesh position={[0, stadiumHeight/2, 0]} castShadow>
            <boxGeometry args={[3, stadiumHeight, 3]} />
            <meshStandardMaterial color="#c0392b" roughness={0.7} />
          </mesh>
          {/* Yellow/beige building section */}
          <mesh position={[0, stadiumHeight/2 - 2, -4]} castShadow>
            <boxGeometry args={[8, stadiumHeight - 4, 8]} />
            <meshStandardMaterial color="#e8d5a3" roughness={0.8} />
          </mesh>
          {/* Red beam accents */}
          <mesh position={[0, stadiumHeight - 1, 0]} castShadow>
            <boxGeometry args={[7, 1.5, 3]} />
            <meshStandardMaterial color="#a93226" roughness={0.6} />
          </mesh>
        </group>
      ))}
      
      {/* Stadium roof - curved oval */}
      <mesh position={[0, stadiumHeight + 2, 0]} castShadow>
        <torusGeometry args={[stadiumRadius + 5, 4, 8, 32, Math.PI]} />
        <meshStandardMaterial color="#d0d0d0" metalness={0.3} roughness={0.4} />
      </mesh>
      
      {/* Stadium roof supports/beams */}
      {seats.filter((_, i) => i % 3 === 0).map((seat, i) => (
        <mesh 
          key={`beam-${i}`} 
          position={[seat.x * 0.7, stadiumHeight + 3, seat.z * 0.7]} 
          rotation={[0, -seat.angle, Math.PI/8]}
          castShadow
        >
          <cylinderGeometry args={[0.3, 0.3, 15, 6]} />
          <meshStandardMaterial color="#8b8b8b" metalness={0.5} roughness={0.4} />
        </mesh>
      ))}
      
      {/* Roof top panels */}
      {seats.filter((_, i) => i % 2 === 0).map((seat, i) => (
        <mesh 
          key={`roof-${i}`}
          position={[seat.x * 0.85, stadiumHeight + 3, seat.z * 0.85]} 
          rotation={[Math.PI/6, -seat.angle - Math.PI/2, 0]}
          castShadow
        >
          <boxGeometry args={[15, 0.5, 8]} />
          <meshStandardMaterial color="#e5e5e5" metalness={0.2} roughness={0.5} />
        </mesh>
      ))}
      
      {/* Flag poles on top */}
      {[-40, -20, 0, 20, 40].map((x, i) => (
        <group key={`flag-${i}`} position={[x, stadiumHeight + 5, 0]}>
          <mesh position={[0, 4, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.15, 8, 6]} />
            <meshStandardMaterial color="#c0c0c0" metalness={0.7} />
          </mesh>
          <mesh position={[1.5, 6, 0]} castShadow>
            <planeGeometry args={[3, 1.8]} />
            <meshStandardMaterial 
              color={['#22c55e', '#3b82f6', '#ec4899', '#f97316', '#22c55e'][i]} 
              side={THREE.DoubleSide}
              roughness={0.6}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/**
 * Entrance Archway - Hari Landskap Negara 2026
 * Matches the actual reference photo: golden bamboo pillars with vine cover,
 * main "SELAMAT DATANG" sign, 4 theme banners hanging, side info panels,
 * green leaf sculptures, flower beds, lanterns
 */
export function EntranceArchway({ position = [0, 0, 90] }) {
  const wineColor = '#8b4513';
  const goldColor = '#c9a961';
  const vineGreen = '#16a34a';
  
  return (
    <group position={position}>
      {/* ============ MAIN ARCH STRUCTURE ============ */}
      {/* Left main pillar - golden bamboo */}
      <group position={[-12, 0, 0]}>
        <mesh position={[0, 5, 0]} castShadow>
          <cylinderGeometry args={[0.5, 0.6, 10, 12]} />
          <meshStandardMaterial color={goldColor} roughness={0.6} metalness={0.3} />
        </mesh>
        {/* Bamboo joints */}
        {[2, 4, 6, 8].map((y, i) => (
          <mesh key={i} position={[0, y, 0]} castShadow>
            <cylinderGeometry args={[0.55, 0.55, 0.3, 12]} />
            <meshStandardMaterial color="#a68143" roughness={0.7} />
          </mesh>
        ))}
        {/* Vine leaf cover */}
        <mesh position={[0, 5, 0]} castShadow>
          <cylinderGeometry args={[0.75, 0.75, 10, 12]} />
          <meshStandardMaterial color={vineGreen} transparent opacity={0.55} roughness={0.95} />
        </mesh>
      </group>
      
      {/* Right main pillar */}
      <group position={[12, 0, 0]}>
        <mesh position={[0, 5, 0]} castShadow>
          <cylinderGeometry args={[0.5, 0.6, 10, 12]} />
          <meshStandardMaterial color={goldColor} roughness={0.6} metalness={0.3} />
        </mesh>
        {[2, 4, 6, 8].map((y, i) => (
          <mesh key={i} position={[0, y, 0]} castShadow>
            <cylinderGeometry args={[0.55, 0.55, 0.3, 12]} />
            <meshStandardMaterial color="#a68143" roughness={0.7} />
          </mesh>
        ))}
        <mesh position={[0, 5, 0]} castShadow>
          <cylinderGeometry args={[0.75, 0.75, 10, 12]} />
          <meshStandardMaterial color={vineGreen} transparent opacity={0.55} roughness={0.95} />
        </mesh>
      </group>
      
      {/* ============ TOP ARCH BEAM ============ */}
      {/* Structural beam */}
      <mesh position={[0, 10, 0]} castShadow>
        <boxGeometry args={[25, 0.8, 2.5]} />
        <meshStandardMaterial color={goldColor} roughness={0.5} metalness={0.4} />
      </mesh>
      
      {/* Dense green vine cover on top */}
      <mesh position={[0, 10.6, 0]} castShadow>
        <boxGeometry args={[25.5, 1.5, 3]} />
        <meshStandardMaterial color={vineGreen} transparent opacity={0.85} roughness={0.9} />
      </mesh>
      
      {/* Hanging vines from top beam */}
      {[-10, -6, -2, 2, 6, 10].map((x, i) => (
        <mesh key={i} position={[x, 9, 0]} castShadow>
          <cylinderGeometry args={[0.08, 0.08, 1.5, 6]} />
          <meshStandardMaterial color="#166534" />
        </mesh>
      ))}
      
      {/* ============ MAIN SIGN ============ */}
      {/* "SELAMAT DATANG - HARI LANDSKAP NEGARA 2026" */}
      <mesh position={[0, 9.2, 1.4]} castShadow>
        <boxGeometry args={[18, 2, 0.1]} />
        <meshStandardMaterial color="#064e3b" emissive="#065f46" emissiveIntensity={0.3} />
      </mesh>
      <Html zIndexRange={[10, 0]} position={[0, 9.2, 1.55]} center distanceFactor={35} occlude={false}>
        <div className="pointer-events-none select-none text-center" style={{
          background: 'transparent',
          padding: '0.5rem 1rem',
        }}>
          <div className="text-white font-bold text-[9px] leading-tight tracking-wider" style={{textShadow: '0 1px 2px rgba(0,0,0,0.8)'}}>
            SELAMAT DATANG
          </div>
          <div className="text-white font-black text-sm leading-tight tracking-wider" style={{textShadow: '0 1px 3px rgba(0,0,0,0.9)'}}>
            HARI LANDSKAP NEGARA 2026
          </div>
        </div>
      </Html>
      
      {/* Theme banner ribbon under sign */}
      <mesh position={[0, 7.8, 1.4]} castShadow>
        <boxGeometry args={[16, 0.7, 0.05]} />
        <meshStandardMaterial color="#22c55e" emissive="#16a34a" emissiveIntensity={0.4} />
      </mesh>
      <Html zIndexRange={[10, 0]} position={[0, 7.8, 1.5]} center distanceFactor={30} occlude={false}>
        <div className="text-white font-bold text-[8px] tracking-widest whitespace-nowrap pointer-events-none" style={{textShadow: '0 1px 2px rgba(0,0,0,0.7)'}}>
          HIJAU • INOVASI • KOMUNITI • LESTARI
        </div>
      </Html>

      {/* ============ 4 VERTICAL THEME BANNERS (hanging between arch) ============ */}
      {[
        {x: -6, color: '#0ea5e9', label: 'HIJAU'},     // Blue banner with HIJAU
        {x: -2, color: '#22c55e', label: 'INOVASI'},   // Green banner
        {x: 2,  color: '#ec4899', label: 'KOMUNITI'},  // Pink banner
        {x: 6,  color: '#f97316', label: 'LESTARI'},   // Orange banner
      ].map((b, i) => (
        <group key={i} position={[b.x, 4, 0]}>
          {/* Banner cloth */}
          <mesh castShadow>
            <planeGeometry args={[1.6, 4]} />
            <meshStandardMaterial color={b.color} side={THREE.DoubleSide} roughness={0.5} emissive={b.color} emissiveIntensity={0.15} />
          </mesh>
          {/* Small label on banner */}
          <Html zIndexRange={[10, 0]} position={[0, 1, 0.1]} center distanceFactor={20} occlude={false}>
            <div className="text-white font-black text-[10px] tracking-wider pointer-events-none" style={{textShadow: '0 1px 2px rgba(0,0,0,0.8)'}}>
              {b.label}
            </div>
          </Html>
        </group>
      ))}
      
      {/* ============ SIDE INFO PANELS ============ */}
      {/* Left panel - PENGANJUR */}
      <group position={[-16, 2, 0]}>
        <mesh castShadow>
          <boxGeometry args={[2.5, 6, 0.15]} />
          <meshStandardMaterial color="#f5f5dc" roughness={0.7} />
        </mesh>
        {/* MBMB logo circle */}
        <mesh position={[0, 2, 0.1]}>
          <cylinderGeometry args={[0.6, 0.6, 0.05, 32]} />
          <meshStandardMaterial color="#dc2626" />
        </mesh>
        <Html zIndexRange={[10, 0]} position={[0, 0, 0.1]} center distanceFactor={15} occlude={false}>
          <div className="pointer-events-none text-center bg-white/95 px-2 py-1 rounded text-slate-800">
            <div className="text-[7px] font-black">PENGANJUR</div>
            <div className="text-[6px] mt-0.5 font-bold text-red-600">MBMB • JLN</div>
            <div className="text-[6px] mt-1 opacity-80">Rakan Kolaborasi:</div>
            <div className="text-[6px] font-bold text-blue-600">MELAKA</div>
          </div>
        </Html>
      </group>
      
      {/* Right panel - Sponsor / Landskap slogan */}
      <group position={[16, 2, 0]}>
        <mesh castShadow>
          <boxGeometry args={[2.5, 6, 0.15]} />
          <meshStandardMaterial color="#f5f5dc" roughness={0.7} />
        </mesh>
        {/* "Mana Lagi? Melaka Lae" logo indicator */}
        <mesh position={[0, 2, 0.1]}>
          <cylinderGeometry args={[0.6, 0.6, 0.05, 32]} />
          <meshStandardMaterial color="#ec4899" />
        </mesh>
        <Html zIndexRange={[10, 0]} position={[0, 0, 0.1]} center distanceFactor={15} occlude={false}>
          <div className="pointer-events-none text-center bg-white/95 px-2 py-1 rounded text-slate-800">
            <div className="text-[6px] font-black leading-tight">LANDSKAP UNTUK</div>
            <div className="text-[6px] font-black leading-tight">KEHIDUPAN BERKUALITI</div>
            <div className="text-[6px] mt-1 opacity-80">Sponsor Utama:</div>
            <div className="text-[7px] font-black text-emerald-700">MALAYSIA MADANI</div>
          </div>
        </Html>
      </group>
      
      {/* ============ BIG DECORATIVE GREEN LEAVES (both sides) ============ */}
      {/* Left leaves */}
      {[
        [-19, 8, 0.5, 0.4, 30],
        [-19, 4, 0.5, -0.3, -20],
        [-18, 11, 0.5, 0.2, 45],
        [-20, 6, 0, 0.5, 0],
      ].map((leaf, i) => (
        <mesh key={`ll-${i}`} position={[leaf[0], leaf[1], leaf[2]]} rotation={[0, leaf[3], leaf[4] * Math.PI / 180]} castShadow>
          <coneGeometry args={[1.5, 4, 4, 1, false, 0, Math.PI * 1.5]} />
          <meshStandardMaterial color={vineGreen} roughness={0.9} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* Right leaves */}
      {[
        [19, 8, 0.5, -0.4, -30],
        [19, 4, 0.5, 0.3, 20],
        [18, 11, 0.5, -0.2, -45],
        [20, 6, 0, -0.5, 0],
      ].map((leaf, i) => (
        <mesh key={`rl-${i}`} position={[leaf[0], leaf[1], leaf[2]]} rotation={[0, leaf[3], leaf[4] * Math.PI / 180]} castShadow>
          <coneGeometry args={[1.5, 4, 4, 1, false, 0, Math.PI * 1.5]} />
          <meshStandardMaterial color={vineGreen} roughness={0.9} side={THREE.DoubleSide} />
        </mesh>
      ))}
      
      {/* ============ FLOWER BEDS AT BASE ============ */}
      {/* Colorful flower rows */}
      {[
        {x: -18, colors: ['#ec4899', '#f59e0b', '#dc2626']},
        {x: -14, colors: ['#f59e0b', '#dc2626', '#ec4899']},
        {x: 14, colors: ['#a855f7', '#f59e0b', '#dc2626']},
        {x: 18, colors: ['#ec4899', '#a855f7', '#dc2626']},
      ].map((bed, i) => (
        <group key={`bed-${i}`} position={[bed.x, 0, 3]}>
          {/* Soil */}
          <mesh position={[0, 0.2, 0]}>
            <boxGeometry args={[3, 0.3, 1.5]} />
            <meshStandardMaterial color="#5c4033" roughness={0.9} />
          </mesh>
          {/* Flowers */}
          {bed.colors.map((c, j) => (
            <mesh key={j} position={[-1 + j, 0.6, 0]}>
              <sphereGeometry args={[0.35, 8, 8]} />
              <meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.15} roughness={0.7} />
            </mesh>
          ))}
        </group>
      ))}
      
      {/* ============ GROUND LANTERNS ============ */}
      {[-8, -4, 4, 8].map((x, i) => (
        <group key={`lantern-${i}`} position={[x, 0, 5]}>
          <mesh position={[0, 0.5, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.2, 1, 6]} />
            <meshStandardMaterial color="#404040" metalness={0.5} />
          </mesh>
          <mesh position={[0, 1.1, 0]}>
            <sphereGeometry args={[0.25, 8, 8]} />
            <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={1.2} />
          </mesh>
          <pointLight position={[0, 1.1, 0]} intensity={0.6} color="#fbbf24" distance={4} />
        </group>
      ))}
      
      {/* ============ FAIRY LIGHTS across top ============ */}
      {[-10, -8, -6, -4, -2, 0, 2, 4, 6, 8, 10].map((x, i) => (
        <group key={`fairy-${i}`} position={[x, 9.5, 1.5]}>
          <mesh>
            <sphereGeometry args={[0.1, 6, 6]} />
            <meshStandardMaterial color="#fef3c7" emissive="#fbbf24" emissiveIntensity={1.5} />
          </mesh>
          <pointLight intensity={0.3} color="#fbbf24" distance={3} />
        </group>
      ))}
    </group>
  );
}

/**
 * Tropical Palm Trees for Malaysian landscape
 */
export function PalmTree({ position, scale = 1 }) {
  return (
    <group position={position} scale={scale}>
      {/* Trunk */}
      <mesh position={[0, 3, 0]} castShadow>
        <cylinderGeometry args={[0.3, 0.5, 6, 8]} />
        <meshStandardMaterial color="#7c5e3c" roughness={0.9} />
      </mesh>
      {/* Palm leaves */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        const x = Math.cos(angle) * 2;
        const z = Math.sin(angle) * 2;
        return (
          <mesh 
            key={i}
            position={[x, 6.5, z]} 
            rotation={[Math.PI * 0.15, angle, 0]}
            castShadow
          >
            <boxGeometry args={[4, 0.15, 1.2]} />
            <meshStandardMaterial color="#16a34a" roughness={0.8} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
      {/* Center leaves */}
      <mesh position={[0, 7, 0]} castShadow>
        <sphereGeometry args={[0.6, 8, 8]} />
        <meshStandardMaterial color="#15803d" roughness={0.9} />
      </mesh>
    </group>
  );
}

/**
 * Malaysian Flag decorations
 */
export function ThemeBanner({ position, color, text }) {
  return (
    <group position={position}>
      {/* Pole */}
      <mesh position={[0, 3, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.1, 6, 6]} />
        <meshStandardMaterial color="#a0a0a0" metalness={0.6} />
      </mesh>
      {/* Vertical banner */}
      <mesh position={[0, 3, 0.15]} castShadow>
        <planeGeometry args={[1, 4]} />
        <meshStandardMaterial color={color} side={THREE.DoubleSide} roughness={0.5} />
      </mesh>
    </group>
  );
}

/**
 * Dense tree line - horizontal barrier separating event area from carpark.
 * Uses varied bushy trees (round canopies) to look like the diagram's tree row.
 */
export function TreeLine({ zStart = -30, zEnd = -45, xMin = -90, xMax = 90 }) {
  const trees = [];
  const centerZ = (zStart + zEnd) / 2;
  const bandDepth = Math.abs(zEnd - zStart);
  let idx = 0;
  for (let x = xMin; x <= xMax; x += 4.5) {
    // 3 staggered rows to make the band dense
    for (let row = 0; row < 3; row++) {
      const jitterX = (Math.sin(idx * 12.9898) * 43758.5453) % 1;
      const jitterZ = (Math.cos(idx * 78.233) * 43758.5453) % 1;
      const px = x + (jitterX - 0.5) * 3;
      const pz = centerZ + (row - 1) * (bandDepth / 3) + (jitterZ - 0.5) * 2;
      const scale = 0.9 + Math.abs(jitterX) * 0.6;
      trees.push({ id: `t-${idx}`, pos: [px, 0, pz], scale });
      idx++;
    }
  }
  return (
    <group>
      {trees.map((t) => (
        <BushyTree key={t.id} position={t.pos} scale={t.scale} />
      ))}
    </group>
  );
}

/**
 * Bushy round tree - dense green canopy on a short trunk
 */
export function BushyTree({ position, scale = 1 }) {
  return (
    <group position={position} scale={scale}>
      {/* Trunk */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <cylinderGeometry args={[0.35, 0.45, 2.4, 6]} />
        <meshStandardMaterial color="#5c4033" roughness={0.9} />
      </mesh>
      {/* Canopy - 3 clustered spheres for organic look */}
      <mesh position={[0, 3.6, 0]} castShadow>
        <sphereGeometry args={[2.2, 10, 8]} />
        <meshStandardMaterial color="#166534" roughness={0.9} />
      </mesh>
      <mesh position={[-1.2, 3.2, 0.8]} castShadow>
        <sphereGeometry args={[1.5, 8, 8]} />
        <meshStandardMaterial color="#15803d" roughness={0.9} />
      </mesh>
      <mesh position={[1.1, 3.4, -0.6]} castShadow>
        <sphereGeometry args={[1.6, 8, 8]} />
        <meshStandardMaterial color="#166534" roughness={0.9} />
      </mesh>
    </group>
  );
}

/**
 * Carpark area - parking lot with marked stalls at the bottom of the scene
 */
export function Carpark({ position = [0, 0, -80], width = 180, depth = 70 }) {
  // Generate rows of parking stalls
  const stallWidth = 5;
  const stallDepth = 10;
  const rowGap = 3; // aisle between rows
  const stallsPerRow = Math.floor((width - 10) / stallWidth);
  const rowCount = Math.floor((depth - 10) / (stallDepth * 2 + rowGap));

  const stalls = [];
  for (let r = 0; r < rowCount; r++) {
    // Two rows back-to-back per bay
    for (let side = 0; side < 2; side++) {
      for (let c = 0; c < stallsPerRow; c++) {
        const x = -width / 2 + 5 + c * stallWidth + stallWidth / 2;
        const zBase = -depth / 2 + 5 + r * (stallDepth * 2 + rowGap);
        const z = zBase + (side === 0 ? stallDepth / 2 : stallDepth + rowGap + stallDepth / 2);
        stalls.push({ id: `s-${r}-${side}-${c}`, x, z });
      }
    }
  }

  // A few decorative parked cars scattered
  const carPositions = stalls.filter((_, i) => i % 7 === 3).slice(0, 24);
  const carColors = ['#dc2626', '#1e40af', '#f5f5f5', '#111827', '#059669', '#d97706'];

  return (
    <group position={position}>
      {/* Dark asphalt base */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, 0]} receiveShadow>
        <planeGeometry args={[width, depth]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.95} />
      </mesh>
      {/* Perimeter kerb */}
      <mesh position={[0, 0.15, -depth / 2 + 0.5]}>
        <boxGeometry args={[width, 0.3, 0.5]} />
        <meshStandardMaterial color="#e5e7eb" />
      </mesh>
      {/* White stall lines */}
      {stalls.map((s) => (
        <mesh key={s.id} rotation={[-Math.PI / 2, 0, 0]} position={[s.x, 0.06, s.z]}>
          <planeGeometry args={[stallWidth - 0.3, stallDepth - 0.4]} />
          <meshBasicMaterial color="#3a3a3a" />
        </mesh>
      ))}
      {/* Stall dividers */}
      {stalls.map((s) => (
        <React.Fragment key={`d-${s.id}`}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[s.x - stallWidth / 2, 0.07, s.z]}>
            <planeGeometry args={[0.15, stallDepth - 0.2]} />
            <meshBasicMaterial color="#ffffff" />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[s.x, 0.07, s.z - stallDepth / 2 + 0.2]}>
            <planeGeometry args={[stallWidth - 0.3, 0.15]} />
            <meshBasicMaterial color="#ffffff" />
          </mesh>
        </React.Fragment>
      ))}
      {/* Decorative parked cars */}
      {carPositions.map((s, i) => (
        <group key={`car-${s.id}`} position={[s.x, 0, s.z]}>
          <mesh position={[0, 0.7, 0]} castShadow>
            <boxGeometry args={[2.2, 1.1, 4.6]} />
            <meshStandardMaterial color={carColors[i % carColors.length]} roughness={0.4} metalness={0.5} />
          </mesh>
          {/* Roof/cabin */}
          <mesh position={[0, 1.35, -0.2]} castShadow>
            <boxGeometry args={[2, 0.7, 2.6]} />
            <meshStandardMaterial color={carColors[i % carColors.length]} metalness={0.6} roughness={0.3} />
          </mesh>
          {/* Windshield glass */}
          <mesh position={[0, 1.35, 1.15]} rotation={[Math.PI / 6, 0, 0]}>
            <planeGeometry args={[1.8, 0.7]} />
            <meshStandardMaterial color="#0f172a" metalness={0.7} roughness={0.1} />
          </mesh>
          {/* Wheels */}
          {[[-0.9, -1.5], [0.9, -1.5], [-0.9, 1.5], [0.9, 1.5]].map(([wx, wz]) => (
            <mesh key={`w-${wx}-${wz}`} position={[wx, 0.35, wz]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.35, 0.35, 0.25, 10]} />
              <meshStandardMaterial color="#111827" />
            </mesh>
          ))}
        </group>
      ))}
      {/* Big "P" parking sign */}
      <group position={[0, 0, -depth / 2 + 2]}>
        <mesh position={[0, 2, 0]} castShadow>
          <cylinderGeometry args={[0.15, 0.15, 4, 6]} />
          <meshStandardMaterial color="#4b5563" metalness={0.5} />
        </mesh>
        <mesh position={[0, 4.5, 0]} castShadow>
          <boxGeometry args={[3, 3, 0.15]} />
          <meshStandardMaterial color="#1d4ed8" />
        </mesh>
        <Html zIndexRange={[10, 0]} position={[0, 4.5, 0.1]} center distanceFactor={30} occlude={false}>
          <div className="text-white font-black text-3xl select-none pointer-events-none" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>
            P
          </div>
        </Html>
      </group>
    </group>
  );
}

/**
 * Facility Zone - a colored ground circle + labeled sign to prominently
 * mark a facility area (surau, tandas, ambulance, food, info, parking).
 * Renders BENEATH/AROUND an existing FacilityMarker to make it obvious.
 */
export function FacilityZone({ position, type, label, radius = 6 }) {
  const config = {
    prayer_room: { color: '#16a34a', bg: '#dcfce7', ring: '#15803d', icon: '🕌', title: 'SURAU' },
    toilet: { color: '#06b6d4', bg: '#cffafe', ring: '#0891b2', icon: '🚻', title: 'TANDAS' },
    first_aid: { color: '#dc2626', bg: '#fee2e2', ring: '#b91c1c', icon: '⛑️', title: 'AMBULANS' },
    food: { color: '#f97316', bg: '#ffedd5', ring: '#c2410c', icon: '🍽️', title: 'MAKANAN' },
    info: { color: '#8b5cf6', bg: '#ede9fe', ring: '#6d28d9', icon: 'ℹ️', title: 'INFO' },
    parking: { color: '#1d4ed8', bg: '#dbeafe', ring: '#1e40af', icon: '🅿️', title: 'PARKIR' },
    entrance: { color: '#eab308', bg: '#fef9c3', ring: '#a16207', icon: '🚪', title: 'PINTU MASUK' },
    shopping: { color: '#ec4899', bg: '#fce7f3', ring: '#be185d', icon: '🛍️', title: 'RERUAI' },
    stadium: { color: '#64748b', bg: '#e2e8f0', ring: '#475569', icon: '🏟️', title: 'STADIUM' },
  };
  const c = config[type] || config.info;

  return (
    <group position={position}>
      {/* Bright ground disc */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.11, 0]}>
        <circleGeometry args={[radius, 32]} />
        <meshStandardMaterial color={c.bg} transparent opacity={0.85} emissive={c.color} emissiveIntensity={0.15} />
      </mesh>
      {/* Outer ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.13, 0]}>
        <ringGeometry args={[radius - 0.3, radius, 48]} />
        <meshStandardMaterial color={c.ring} emissive={c.ring} emissiveIntensity={0.5} />
      </mesh>
      {/* Big floating icon disk (like Google Maps pin) */}
      <group position={[0, 4.5, 0]}>
        <mesh castShadow>
          <cylinderGeometry args={[1.4, 1.4, 0.4, 24]} />
          <meshStandardMaterial color={c.color} emissive={c.color} emissiveIntensity={0.5} />
        </mesh>
        {/* Point at bottom */}
        <mesh position={[0, -0.6, 0]} rotation={[0, 0, 0]}>
          <coneGeometry args={[0.5, 1.2, 16]} />
          <meshStandardMaterial color={c.color} emissive={c.color} emissiveIntensity={0.5} />
        </mesh>
        <Html zIndexRange={[10, 0]} position={[0, 0.3, 0]} center distanceFactor={18} occlude={false}>
          <div className="text-4xl pointer-events-none select-none" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.6))' }}>
            {c.icon}
          </div>
        </Html>
      </group>
      {/* Text label board on ground */}
      <group position={[0, 0.15, radius - 1]}>
        <Html zIndexRange={[10, 0]} position={[0, 0.05, 0]} center distanceFactor={16} occlude={false}>
          <div
            className="px-3 py-1 rounded-md text-white font-black text-[9px] tracking-widest select-none pointer-events-none whitespace-nowrap"
            style={{ backgroundColor: c.ring, boxShadow: '0 4px 12px rgba(0,0,0,0.35)', border: `2px solid ${c.color}` }}
          >
            {label || c.title}
          </div>
        </Html>
      </group>
      {/* Ambient light so the marker pops at night */}
      <pointLight position={[0, 4.5, 0]} intensity={0.4} color={c.color} distance={12} />
    </group>
  );
}
